/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { X, Check, Star, MapPin, AlertCircle } from 'lucide-react';
import axios, { API } from '../../utils/api';
import { useCommunityAuth } from '../../context/CommunityAuthContext';

const COMM = `${API}/community`;
const TEAL = '#4DB6AC';

/**
 * Shared modal for announcement / quiz / poll items from `community_actions`.
 * Mode is derived from `action.action_type`. The poll voting UI is reused for
 * both poll and quiz — the only difference is what we display after submission
 * (instant correct/incorrect feedback for quiz, live percentages for poll).
 */
export default function CommunityActionModal({ action, onClose, onResponded }) {
  const { token, refreshUser } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [selected, setSelected] = useState(() => {
    const prior = action?.my_response?.selected_indices;
    return new Set(prior || []);
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(action?.my_response || null);
  const [tally, setTally] = useState(action?.tally || null);
  const [totalVotes, setTotalVotes] = useState(action?.total_votes ?? null);
  const [err, setErr] = useState('');

  if (!action) return null;
  const type = action.action_type;
  const options = action.options || [];
  const correctSet = new Set(action.correct_indices || result?.correct_indices || []);
  // For quiz we only allow a single pick on the UI. Backend supports multi but
  // the product spec says "after selecting an option" — singular — so keep it
  // single-pick for both. Poll also single-pick.
  const togglePick = (i) => {
    if (result) return;
    setSelected(new Set([i]));
  };

  const submit = async () => {
    if (selected.size === 0) { setErr('Pick an option first.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const res = await axios.post(`${COMM}/actions/${action.id}/respond`,
        { selected_indices: [...selected] }, authHeaders);
      setResult(res.data);
      if (res.data?.tally) setTally(res.data.tally);
      if (typeof res.data?.total_votes === 'number') setTotalVotes(res.data.total_votes);
      if (res.data?.stars_awarded > 0 && refreshUser) refreshUser();
      if (onResponded) onResponded(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Announcement view ──
  if (type === 'announcement') {
    return (
      <Shell onClose={onClose} testid="announcement-modal">
        {action.image_url && (
          <img src={action.image_url} alt={action.title}
            style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 12, marginBottom: 16 }} />
        )}
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', margin: '0 0 8px' }}>{action.title}</h2>
        {action.description && (
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: '0 0 12px', whiteSpace: 'pre-wrap' }}>{action.description}</p>
        )}
        {action.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280' }}>
            <MapPin size={14} /> {action.location}
          </div>
        )}
      </Shell>
    );
  }

  // ── Quiz / Poll view (shared UI) ──
  const isQuiz = type === 'quiz';
  const showResults = !!result;
  const showCorrectness = isQuiz && showResults;
  const showTally = !isQuiz && showResults && tally;

  return (
    <Shell onClose={onClose} testid={isQuiz ? 'quiz-modal' : 'poll-modal'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
          padding: '4px 10px', borderRadius: 12,
          background: isQuiz ? 'rgba(139,92,246,0.12)' : 'rgba(245,158,11,0.14)',
          color: isQuiz ? '#7C3AED' : '#B45309',
        }}>
          {isQuiz ? 'Quiz' : 'Poll'}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '4px 8px', borderRadius: 10 }}>
          <Star size={12} fill="#F59E0B" color="#F59E0B" />+5
        </span>
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', margin: '0 0 14px', lineHeight: 1.4 }}>
        {action.question || action.title}
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {options.map((opt, i) => {
          const isPicked = selected.has(i) || result?.selected_indices?.includes(i);
          const isCorrect = correctSet.has(i);
          const wasMyPick = result?.selected_indices?.includes(i);
          let bg = '#FFF';
          let border = '#E5E7EB';
          let icon = null;
          if (showCorrectness) {
            if (isCorrect) { bg = 'rgba(34,197,94,0.10)'; border = '#22C55E'; icon = <Check size={16} color="#22C55E" />; }
            else if (wasMyPick && !isCorrect) { bg = 'rgba(239,68,68,0.10)'; border = '#EF4444'; icon = <X size={16} color="#EF4444" />; }
          } else if (isPicked) {
            bg = `${TEAL}15`; border = TEAL;
          }
          const pct = showTally && totalVotes > 0 ? Math.round((tally[i] / totalVotes) * 100) : 0;
          return (
            <button key={i} type="button" onClick={() => togglePick(i)} disabled={showResults}
              data-testid={`action-option-${i}`}
              style={{
                position: 'relative', overflow: 'hidden',
                textAlign: 'left', padding: '12px 14px', borderRadius: 10,
                border: `1.5px solid ${border}`, background: bg,
                fontSize: 14, color: '#1F2937',
                cursor: showResults ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                transition: 'all 0.15s',
              }}>
              {showTally && (
                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to right, ${TEAL}22 ${pct}%, transparent ${pct}%)`, zIndex: 0 }} />
              )}
              <span style={{ position: 'relative', flex: 1, fontWeight: isPicked ? 600 : 400 }}>{opt}</span>
              {icon && <span style={{ position: 'relative' }}>{icon}</span>}
              {showTally && (
                <span style={{ position: 'relative', fontSize: 12, fontWeight: 700, color: '#374151' }}>{pct}%</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Feedback strip after submission */}
      {showResults && (
        <div data-testid="action-result-banner"
          style={{ padding: '10px 12px', borderRadius: 10, marginBottom: 12,
            background: isQuiz ? (result.is_correct ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)') : `${TEAL}15`,
            border: `1px solid ${isQuiz ? (result.is_correct ? '#22C55E' : '#EF4444') : TEAL}`,
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          {isQuiz ? (
            result.is_correct
              ? <><Check size={16} color="#16A34A" /><span style={{ color: '#166534', fontWeight: 600 }}>Correct! +5 stars credited.</span></>
              : <><AlertCircle size={16} color="#DC2626" /><span style={{ color: '#991B1B', fontWeight: 600 }}>Not quite. Better luck next time.</span></>
          ) : (
            <><Check size={16} color={TEAL} /><span style={{ color: '#155E59', fontWeight: 600 }}>Vote recorded. +5 stars credited.</span></>
          )}
        </div>
      )}

      {err && (
        <div style={{ padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 12 }}>{err}</div>
      )}

      {!showResults && (
        <button onClick={submit} disabled={submitting || selected.size === 0}
          data-testid="action-submit-btn"
          style={{ width: '100%', padding: '12px', background: submitting || selected.size === 0 ? '#9CA3AF' : TEAL,
            color: '#FFF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
            cursor: submitting || selected.size === 0 ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      )}
    </Shell>
  );
}

function Shell({ children, onClose, testid }) {
  return (
    <div onClick={onClose} data-testid={testid}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#FFF', borderRadius: 16, padding: 20, width: '100%', maxWidth: 420, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button onClick={onClose} data-testid="action-modal-close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
